import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../services/api';
import { useApp } from '../context/AppContext';

const JokesPage = () => {
    const navigate = useNavigate();
    const { showToast } = useApp();
    const [allJokes, setAllJokes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [category, setCategory] = useState('all');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [likedJokes, setLikedJokes] = useState({});

    useEffect(() => {
        fetchAllJokes();
    }, [category, page]);

    const fetchAllJokes = async () => {
        setLoading(true);
        try {
            const [articlesRes, jokesRes] = await Promise.all([
                api.get('/articles?category=Jokes&status=live'),
                api.get('/jokes?status=published')
            ]);

            const articleJokes = articlesRes.data.map(article => ({
                _id: article._id,
                source: 'article',
                title: article.title,
                text: article.excerpt || article.content?.replace(/<[^>]*>/g, '').substring(0, 200),
                content: article.content,
                category: article.category,
                image: article.img,
                imageData: article.imgData,
                likes: article.analytics?.totalShares || 0,
                views: article.views || 0,
                date: article.date || article.createdAt,
                createdAt: article.createdAt
            }));

            const jokesData = jokesRes.data.jokes || jokesRes.data;
            const managerJokes = (Array.isArray(jokesData) ? jokesData : []).map(joke => ({
                _id: joke._id,
                source: 'manager',
                title: joke.title || `Joke ${joke._id}`,
                text: joke.text,
                category: joke.category,
                image: joke.image,
                imageData: joke.imageData,
                likes: joke.likes || 0,
                views: joke.views || 0,
                date: joke.createdAt,
                createdAt: joke.createdAt
            }));

            let combined = [...articleJokes, ...managerJokes];
            combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

            if (category !== 'all') {
                combined = combined.filter(joke =>
                    joke.category?.toLowerCase() === category.toLowerCase()
                );
            }

            const itemsPerPage = 9;
            const startIndex = (page - 1) * itemsPerPage;
            const paginatedJokes = combined.slice(startIndex, startIndex + itemsPerPage);

            setAllJokes(paginatedJokes);
            setTotalPages(Math.ceil(combined.length / itemsPerPage));

            const liked = {};
            combined.forEach(joke => {
                const likedKey = `joke_liked_${joke._id}`;
                liked[joke._id] = localStorage.getItem(likedKey) === 'true';
            });
            setLikedJokes(liked);

        } catch (error) {
            console.error('Error fetching jokes:', error);
            showToast('Error loading jokes', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleLike = async (jokeId, source) => {
        if (likedJokes[jokeId]) {
            showToast('You already liked this joke!', 'info');
            return;
        }

        try {
            let res;
            if (source === 'article') {
                res = await api.post(`/articles/${jokeId}/like`);
            } else {
                res = await api.post(`/jokes/${jokeId}/like`);
            }

            if (!res.data.alreadyLiked) {
                setLikedJokes(prev => ({ ...prev, [jokeId]: true }));
                localStorage.setItem(`joke_liked_${jokeId}`, 'true');
                showToast('👍 You liked this joke!');

                setAllJokes(prev => prev.map(joke =>
                    joke._id === jokeId
                        ? { ...joke, likes: (joke.likes || 0) + 1 }
                        : joke
                ));
            }
        } catch (error) {
            console.error('Error liking joke:', error);
            showToast('Error liking joke', 'error');
        }
    };

    const openJoke = (jokeId, source) => {
        if (source === 'article') {
            navigate(`/article/${jokeId}`);
        } else {
            navigate(`/joke/${jokeId}`);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Recently';
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.ceil((now - date) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Today';
        if (diffDays === 1) return 'Yesterday';
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
        return `${Math.floor(diffDays / 30)} months ago`;
    };

    const stripHtml = (html) => {
        if (!html) return '';
        const temp = document.createElement('div');
        temp.innerHTML = html;
        return temp.textContent || temp.innerText || '';
    };

    const categories = [
        { value: 'all', label: 'All Jokes', icon: '😄' },
        { value: 'general', label: 'General', icon: '😂' },
        { value: 'programming', label: 'Programming', icon: '💻' },
        { value: 'dad', label: 'Dad Jokes', icon: '👨' },
        { value: 'chuck', label: 'Chuck Norris', icon: '🥋' },
        { value: 'Jokes', label: 'Article Jokes', icon: '📰' },
    ];

    // Generate schema for jokes listing page
    const generateListSchema = () => ({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": "Jokes Archive",
        "description": "Get your daily dose of laughter with our collection of funny jokes. From dad jokes to programming humor, we've got something for everyone.",
        "url": window.location.href,
        "numberOfItems": allJokes.length,
        "mainEntity": {
            "@type": "ItemList",
            "itemListElement": allJokes.map((joke, index) => ({
                "@type": "ListItem",
                "position": index + 1,
                "url": joke.source === 'article'
                    ? `${window.location.origin}/article/${joke._id}`
                    : `${window.location.origin}/joke/${joke._id}`
            }))
        }
    });

    if (loading && page === 1) {
        return (
            <div className="loader" style={{ minHeight: '60vh' }}>
                <div className="loader-spinner"></div>
            </div>
        );
    }

    return (
        <>
            <Helmet>
                {/* Basic SEO */}
                <title>{category === 'all' ? 'Funny Jokes Collection' : `${category.charAt(0).toUpperCase() + category.slice(1)} Jokes`} | Wistar News</title>
                <meta name="description" content={`Get your daily dose of laughter with our collection of ${category === 'all' ? 'funny jokes' : category.toLowerCase() + ' jokes'}. Share, like, and enjoy hand-picked humor.`} />
                <meta name="keywords" content={`jokes, funny jokes, ${category !== 'all' ? category.toLowerCase() + ' jokes,' : ''} daily laugh, comedy, humor, entertainment`} />
                <link rel="canonical" href={window.location.href} />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:title" content={category === 'all' ? 'Jokes Archive - Funny Jokes Collection' : `${category.charAt(0).toUpperCase() + category.slice(1)} Jokes - Wistar News`} />
                <meta property="og:description" content={`Get your daily dose of laughter with our collection of ${category === 'all' ? 'funny jokes' : category.toLowerCase() + ' jokes'}.`} />
                <meta property="og:image" content="https://wistarnews.com/jokes-og-image.jpg" />
                <meta property="og:url" content={window.location.href} />
                <meta property="og:site_name" content="Wistar News" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={category === 'all' ? 'Jokes Archive - Funny Jokes Collection' : `${category.charAt(0).toUpperCase() + category.slice(1)} Jokes`} />
                <meta name="twitter:description" content={`Get your daily dose of laughter with our collection of ${category === 'all' ? 'funny jokes' : category.toLowerCase() + ' jokes'}.`} />
                <meta name="twitter:image" content="https://wistarnews.com/jokes-og-image.jpg" />

                {/* JSON-LD Schema */}
                <script type="application/ld+json">
                    {JSON.stringify(generateListSchema())}
                </script>
            </Helmet>

            <div className="page" style={{ paddingTop: '20px' }}>
                {/* Hero Section */}
                <div style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: '12px',
                    padding: '40px',
                    marginBottom: '30px',
                    textAlign: 'center',
                    color: '#fff'
                }}>
                    <h1 style={{ fontSize: '48px', marginBottom: '10px' }}>😂 Jokes Archive</h1>
                    <p style={{ fontSize: '18px', opacity: 0.9 }}>Get your daily dose of laughter! Share, like, and enjoy our collection of hand-picked jokes.</p>
                </div>

                {/* Category Filter */}
                <div style={{
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap',
                    marginBottom: '30px',
                    justifyContent: 'center'
                }}>
                    {categories.map(cat => (
                        <button
                            key={cat.value}
                            onClick={() => {
                                setCategory(cat.value);
                                setPage(1);
                            }}
                            style={{
                                padding: '10px 20px',
                                background: category === cat.value ? 'var(--red)' : '#f0f0f0',
                                color: category === cat.value ? '#fff' : '#333',
                                border: 'none',
                                borderRadius: '30px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '600',
                                transition: 'all 0.2s'
                            }}
                        >
                            {cat.icon} {cat.label}
                        </button>
                    ))}
                </div>

                {/* Source Badge Info */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '20px',
                    marginBottom: '20px',
                    fontSize: '12px'
                }}>
                    <span><span style={{ background: '#e8f5e9', padding: '2px 8px', borderRadius: '10px' }}>📰 Article Jokes</span> - From articles with category "Jokes"</span>
                    <span><span style={{ background: '#fff3e0', padding: '2px 8px', borderRadius: '10px' }}>😂 Manager Jokes</span> - From Jokes Manager</span>
                </div>

                {/* Jokes Grid */}
                {allJokes.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', background: '#fff', borderRadius: '12px' }}>
                        <h3>No jokes found in this category</h3>
                        <p>Check back later for more funny content!</p>
                    </div>
                ) : (
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                        gap: '24px',
                        marginBottom: '40px'
                    }}>
                        {allJokes.map(joke => (
                            <div
                                key={joke._id}
                                onClick={() => openJoke(joke._id, joke.source)}
                                style={{
                                    background: '#fff',
                                    borderRadius: '12px',
                                    overflow: 'hidden',
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s, box-shadow 0.2s',
                                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                    border: '1px solid #eee'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-4px)';
                                    e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.12)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                                }}
                            >
                                {/* Source Badge */}
                                <div style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    zIndex: 1
                                }}>
                                    <span style={{
                                        background: joke.source === 'article' ? '#e8f5e9' : '#fff3e0',
                                        color: joke.source === 'article' ? '#2e7d32' : '#e65100',
                                        padding: '2px 10px',
                                        borderRadius: '20px',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                    }}>
                                        {joke.source === 'article' ? '📰 Article' : '😂 Joke'}
                                    </span>
                                </div>

                                {joke.image || joke.imageData ? (
                                    <div style={{ height: '200px', overflow: 'hidden' }}>
                                        <img
                                            src={joke.image || joke.imageData}
                                            alt={joke.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    </div>
                                ) : (
                                    <div style={{
                                        height: '120px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '48px'
                                    }}>
                                        {joke.category === 'programming' ? '💻' :
                                            joke.category === 'dad' ? '👨' :
                                                joke.category === 'chuck' ? '🥋' : '😂'}
                                    </div>
                                )}
                                <div style={{ padding: '20px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                        <span className="tag" style={{ fontSize: '11px' }}>{joke.category}</span>
                                        <span style={{ fontSize: '11px', color: '#888' }}>{formatDate(joke.createdAt)}</span>
                                    </div>
                                    <h3 style={{
                                        fontSize: '18px',
                                        marginBottom: '10px',
                                        color: 'var(--navy)',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}>
                                        {joke.title || `😂 Joke`}
                                    </h3>
                                    <p style={{
                                        fontSize: '14px',
                                        lineHeight: '1.6',
                                        color: '#555',
                                        marginBottom: '15px',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden'
                                    }}>
                                        {joke.text || stripHtml(joke.content || '').substring(0, 150)}
                                    </p>
                                    <div style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        borderTop: '1px solid #eee',
                                        paddingTop: '15px'
                                    }}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleLike(joke._id, joke.source);
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '5px',
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: likedJokes[joke._id] ? '#ff6b6b' : '#888',
                                                fontSize: '14px'
                                            }}
                                        >
                                            👍 {joke.likes || 0}
                                        </button>
                                        <span style={{ fontSize: '12px', color: '#888' }}>
                                            👁️ {joke.views || 0} views
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px', marginBottom: '40px' }}>
                        <button
                            onClick={() => setPage(p => Math.max(1, p - 1))}
                            disabled={page === 1}
                            style={{
                                padding: '8px 16px',
                                background: page === 1 ? '#f0f0f0' : 'var(--navy)',
                                color: page === 1 ? '#999' : '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: page === 1 ? 'not-allowed' : 'pointer'
                            }}
                        >
                            ← Previous
                        </button>
                        <span style={{ padding: '8px 16px', background: '#f0f0f0', borderRadius: '5px' }}>
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                            disabled={page === totalPages}
                            style={{
                                padding: '8px 16px',
                                background: page === totalPages ? '#f0f0f0' : 'var(--navy)',
                                color: page === totalPages ? '#999' : '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: page === totalPages ? 'not-allowed' : 'pointer'
                            }}
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default JokesPage;